// Client portal session context — "Septima Link" (PRD #71).
//
// Wraps the portal credential auth: on mount the provider asks the server for
// the current portal session (resolved from the `septima-portal-session`
// cookie). Portal pages read `usePortalSession()` to gate their content; the
// layout route redirects to `/portal/login` when there is no live session.
//
// `login` signs in with a per-client slug + password; `logout` ends the session.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { ClientPortalSession } from '@septima/contract';
import { api } from '@/lib/api';

interface PortalSessionState {
    session: ClientPortalSession | null;
    loading: boolean;
    /** Sign in with a portal slug + password. Returns the session on success, null on failure. */
    login: (slug: string, password: string) => Promise<ClientPortalSession | null>;
    /** End the current session. */
    logout: () => Promise<void>;
    /** Re-fetch the session from the server. */
    refresh: () => Promise<void>;
}

const PortalSessionContext = createContext<PortalSessionState | null>(null);

export function PortalSessionProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<ClientPortalSession | null>(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const res = await api.portal.getSession();
            setSession(res.status === 200 ? res.body.session : null);
        } catch {
            // A network failure is the same as a signed-out session.
            setSession(null);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            let next: ClientPortalSession | null = null;
            try {
                const res = await api.portal.getSession();
                next = res.status === 200 ? res.body.session : null;
            } catch {
                // A network failure is the same as a signed-out session.
            }
            if (cancelled) return;
            setSession(next);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback(
        async (slug: string, password: string): Promise<ClientPortalSession | null> => {
            const res = await api.portal.login({ body: { slug, password } });
            if (res.status === 200 && res.body.session) {
                setSession(res.body.session);
                return res.body.session;
            }
            return null;
        },
        [],
    );

    const logout = useCallback(async () => {
        await api.portal.logout({ body: {} });
        setSession(null);
    }, []);

    return (
        <PortalSessionContext.Provider value={{ session, loading, login, logout, refresh }}>
            {children}
        </PortalSessionContext.Provider>
    );
}

export function usePortalSession(): PortalSessionState {
    const ctx = useContext(PortalSessionContext);
    if (!ctx) {
        throw new Error('usePortalSession must be used within a PortalSessionProvider');
    }
    return ctx;
}
